<template>
  <div class="relative items-center p-4 bg-white dark:bg-slate-900">
    <div class="text-left rtl:text-right">
      <div class="flex justify-between flex-row">
        <thumbnail
          v-if="showAvatar"
          :src="contact.thumbnail"
          size="56px"
          :username="contact.name"
          :status="contact.availability_status"
          :enabled-status-indicator="!isAnEmailChannel"
        />
        <woot-button
          v-if="showCloseButton"
          :icon="closeIconName"
          class="clear secondary rtl:rotate-180"
          @click="onPanelToggle"
        />
      </div>

      <div class="mt-2 w-full">
        <div v-if="showAvatar" class="flex items-center mb-2 gap-1.5">
          <i
            v-if="contactBlocked"
            v-tooltip.top-start="$t('CONTACT_PANEL.BLOCKED_SUCCESS')"
            class="ion-sad-outline"
            style="color: red; margin-inline-end: 0.4rem"
          />
          <h3
            class="text-base text-slate-800 dark:text-slate-100 capitalize whitespace-normal my-0"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-start gap-1">
            <fluent-icon
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              icon="info"
              size="14"
              class="mt-0.5"
            />
            <woot-button
              size="tiny"
              icon="open"
              variant="clear"
              color-scheme="secondary"
              @click="$router.push(contactProfileLink)"
            />
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words">
          {{ additionalAttributes.description }}
        </p>
        <social-icons :social-profiles="socialProfiles" />
        <div class="mb-3">
          <contact-info-row
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.COPY_EMAIL_ADDRESS')"
            show-copy
          />
          <contact-info-row
            :href="`tel:${contact.phone_number}`"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.COPY_PHONE_NUMBER')"
            show-copy
            @click.native="handlePhoneClick($event)"
          />
          <contact-info-row
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <contact-info-row
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
          />
          <contact-info-row
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
        </div>
      </div>
      <div class="flex items-center w-full mt-2 gap-2">
        <woot-button
          v-tooltip="$t('CONTACT_PANEL.NEW_MESSAGE')"
          title="$t('CONTACT_PANEL.NEW_MESSAGE')"
          icon="chat"
          size="small"
          @click="toggleConversationModal"
        />
        <woot-button
          v-if="canEditContact"
          v-tooltip="$t('EDIT_CONTACT.BUTTON_LABEL')"
          title="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="edit"
          variant="smooth"
          size="small"
          @click="toggleEditModal"
        />
        <woot-button
          v-if="canEditContact"
          v-tooltip="$t('CONTACT_PANEL.MERGE_CONTACT')"
          title="$t('CONTACT_PANEL.MERGE_CONTACT')"
          icon="merge"
          variant="smooth"
          size="small"
          color-scheme="secondary"
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <woot-button
          v-if="hasDeletePermission('Contacts')"
          v-tooltip="$t('DELETE_CONTACT.BUTTON_LABEL')"
          title="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="delete"
          variant="smooth"
          size="small"
          color-scheme="alert"
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
        <div v-if="hasEditPermission('Contacts')">
          <woot-button
            v-if="blockContactBtnDisabled"
            v-tooltip="
              contactPageBlockContactBtnDisabled
                ? $t('CONTACT_PANEL.DISABLE_WHATSAPP_BLOCK')
                : $t('CONTACT_PANEL.UNABLE_BLOCK')
            "
            icon="speaker-mute"
            class="merge-contact"
            variant="smooth"
            size="small"
            color-scheme="secondary"
            disabled
          />
          <woot-button
            v-else-if="!contactBlocked"
            v-tooltip="$t('CONTACT_PANEL.BLOCK_CONTACT')"
            icon="speaker-mute"
            class="merge-contact"
            variant="smooth"
            size="small"
            color-scheme="secondary"
            @click="blockContact"
          />
          <woot-button
            v-else
            v-tooltip="$t('CONTACT_PANEL.UNBLOCK_CONTACT')"
            class="merge-contact"
            variant="smooth"
            size="small expanded"
            color-scheme="secondary"
            icon="speaker-1"
            @click="unBlockContact"
          />
        </div>
        <woot-button
          v-if="canEditContact"
          class="merge-contact"
          size="small expanded"
          v-tooltip="$t('NOTES.HEADER.TITLE')"
          icon="clipboard"
          variant="smooth"
          color-scheme="secondary"
          @click="$router.push(contactProfileLink)"
        />
        <woot-button
          v-if="showDocalButton"
          v-tooltip="$t('DOCAL_UNSIGNED_TOOLTIP')"
          icon="document"
          variant="smooth"
          size="small"
          @click="toggleUnsignedDocuments"
        />
      </div>
      <docal-unsigned-modal
        :show="showUnsignedDocuments"
        :contact-id="this.contact.id"
        @close="toggleUnsignedDocuments"
      />
      <edit-contact
        v-if="showEditModal && canEditContact"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <new-conversation
        v-if="contact.id"
        :show="showConversationModal"
        :contact="contact"
        @cancel="toggleConversationModal"
      />
      <contact-merge-modal
        v-if="showMergeModal && canEditContact"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="toggleMergeModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal && hasDeletePermission('Contacts')"
      :show.sync="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
    <block-contact-modal ref="confirmDialog" @change="onChangeBlockDuration" />
  </div>
</template>
<script>
import { mixin as clickaway } from 'vue-clickaway';
import timeMixin from 'dashboard/mixins/time';
import ContactInfoRow from './ContactInfoRow.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import NewConversation from './NewConversation.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import DocalUnsignedModal from './DocalUnsignedModal.vue';
import alertMixin from 'shared/mixins/alertMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';

import Click2CallSettingsAPI from '../../../../api/click2callSettings';
import { mapGetters } from 'vuex';
import { getCountryFlag } from 'dashboard/helper/flag';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import ConversationApi from 'dashboard/api/inbox/conversation';
import BlockContactModal from './BlockContactModal.vue';
export default {
  components: {
    ContactInfoRow,
    EditContact,
    Thumbnail,
    SocialIcons,
    NewConversation,
    ContactMergeModal,
    BlockContactModal,
    DocalUnsignedModal,
  },
  mixins: [alertMixin, hasPermissionMixin, clickaway, timeMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    channelType: {
      type: String,
      default: '',
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    closeIconName: {
      type: String,
      default: 'chevron-right',
    },
    preventBlockContact: {
      type: Boolean,
      default: false,
    },
    inConversationBox: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showEditModal: false,
      showConversationModal: false,
      showMergeModal: false,
      showDeleteModal: false,
      blockDuration: null,
      showUnsignedDocuments: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
      getAgent: 'agents/getAgent',
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      getAttributesByModel: 'attributes/getAttributesByModel',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    contactKeys() {
      return {
        phone_number: this.contact?.phone_number,
        email: this.contact?.email,
        company_name: this.contact?.additional_attributes?.company_name,
        name: this.contact?.name,
      };
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    isAnEmailChannel() {
      return this.channelType === INBOX_TYPES.EMAIL;
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
      } = this.additionalAttributes;

      return { twitter: twitterScreenName, ...(socialProfiles || {}) };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    account() {
      return this.getAccount(this.accountId);
    },
    newRulesSystemEnabled() {
      return this.account?.features?.new_rules_system;
    },
    canEditContact() {
      return !this.newRulesSystemEnabled || this.hasEditPermission('Contacts');
    },
    contactBlocked() {
      return this.contact?.blocked;
    },
    contactPageBlockContactBtnDisabled() {
      return !this.inConversationBox && this.contact.is_whatsapp_contact;
    },
    blockContactBtnDisabled() {
      return (
        this.contactPageBlockContactBtnDisabled ||
        (this.preventBlockContact && !this.contact.blocked)
      );
    },
    showDocalButton() {
      return this.account?.additional_attributes?.document_signature_enabled;
    },
    hasModalOpen() {
      return (
        this.showConversationModal ||
        this.showEditModal ||
        this.showMergeModal ||
        this.showUnsignedDocuments ||
        this.showDeleteModal
      );
    },
  },
  watch: {
    hasModalOpen(value) {
      if (value) {
        this.updateZindex(20);
      } else {
        setTimeout(() => {
          this.updateZindex(10);
        }, 300);
      }
    },
  },
  methods: {
    addCallNote(){
      const now = new Date();
      const formattedDate = now.toLocaleDateString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      const formattedTime = now.toLocaleTimeString('he-IL', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const content = this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.NOTE_LOG.NOTE_CONTENT`, {
        date: formattedDate,
        time: formattedTime,
        phone: this.contact.phone_number,
      });
      const contactId = this.contact.id;
      this.$store.dispatch('contactNotes/create', { content, contactId });
    },
    isClick2CallEnabled(payload){
      if(payload?.data?.click2call_enabled == true)
        return true;
      return false;
    },
    async handlePhoneClick(event) {
      // prevent instant tel
      event.preventDefault();
      const phone = this.contact.phone_number;
      const response = await Click2CallSettingsAPI.getSettings();
      if(!this.isClick2CallEnabled(response)){
        window.location.href = `tel:${phone}`;
        return;
      }
      try {
        if(!confirm(this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.CONFIRM`))) return;
        // get conversation Attributes
        const conversationId = this.currentChat.id;
        const response0 = await ConversationApi.getSingleConversation(conversationId);
        const conversationAttributes = response0.data.custom_attributes || {};
        // contact custom Attributes
        const contactAttributes = this.contact.custom_attributes || {};

        // agent Attributes
        const currentAgent = this.getAgent(this.currentUser.id);
        const agentAttributes = currentAgent.custom_attributes || {};
        // Contact Variables
        const contactKeys = this.contactKeys;

        const data = response.data;
        const method = data.method;  // POST/GET
        const base_url = data.base_url;
        const isEnable = data.enable; // Click2Call Settings enabled

        const selected_parameters = data.selected_parameters;
        if(!data || !method || !base_url || !isEnable){
          return this.showAlert(this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.FAIL`));
        }
        // Build the payload with parameters from the settings

        const payload = {};
        if (Array.isArray(selected_parameters)) {
          selected_parameters.forEach(param => {
            const source = param.value;
            let value = null;

            if(source?.includes("phone_number")){
              let tmpAttribute = "phone_number";
              if(source === "phone_number_+972")
                value = contactKeys[tmpAttribute];
              else if(source === "phone_number_972")
                value = contactKeys[tmpAttribute]?.slice(1);
              else if(source === "phone_number_05")
                value = contactKeys[tmpAttribute]?.replace("+972","0");
            }
            else{
              if (source in contactKeys) {
                value = contactKeys[source];
              } else if (source in conversationAttributes) {
                value = conversationAttributes[source];
              } else if (source in contactAttributes) {
                value = contactAttributes[source];
              } else if (source in agentAttributes) {
                value = agentAttributes[source];
              }
            }
            if (value !== null && value !== undefined) {
              payload[param.key] = value;
            }
          });
        }

        const upperMethod = method.toUpperCase();
        if (!upperMethod || !['GET', 'POST'].includes(upperMethod)) {
          console.warn("Wrong Method");
        }
        const response2 = await Click2CallSettingsAPI.triggerClick2Call({
          url: base_url,
          method: upperMethod,
          payload: payload,
          });
        if(response2 && response?.status !== 200){
          this.showAlert(this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.FAIL`));
          return;
        }

        this.showAlert(`Calling ${phone} via Click2Call...`);
        this.addCallNote();
      } catch (error) {
          console.error(error);
          this.showAlert(this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.FAIL`));
      }
    },

    toggleMergeModal() {
      this.showMergeModal = !this.showMergeModal;
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    onPanelToggle() {
      this.$emit('toggle-panel');
    },
    toggleConversationModal() {
      this.showConversationModal = !this.showConversationModal;
      bus.$emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, this.showConversationModal);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showConversationModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        const countryFlag = countryCode ? getCountryFlag(countryCode) : '🌎';
        return `${cityAndCountry} ${countryFlag}`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panel-close');
        this.showAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    openMergeModal() {
      this.toggleMergeModal();
    },
    async blockContact() {
      const ok = await this.$refs.confirmDialog.showConfirmation();
      if (ok && this.blockDuration !== null) {
        this.$store.dispatch('contacts/blockContact', {
          contactId: this.contact.id,
          conversationId: this.currentChat.id,
          period: this.blockDuration.id,
        });
        this.showAlert(this.$t('CONTACT_PANEL.BLOCKED_SUCCESS'));
      }
    },
    unBlockContact() {
      this.$store.dispatch('contacts/unblockContact', {
        contactId: this.contact.id,
        conversationId: this.currentChat.id,
      });
      this.showAlert(this.$t('CONTACT_PANEL.UNBLOCKED_SUCCESS'));
    },
    onChangeBlockDuration(duration) {
      this.blockDuration = duration;
    },
    toggleUnsignedDocuments() {
      this.showUnsignedDocuments = !this.showUnsignedDocuments;
    },
    updateZindex(value) {
      const el = document.querySelector('.conversation-sidebar-wrap');
      el.style.zIndex = value;
    },
  },
};
</script>
